import type {
  JobHistoryConfig,
  JobHistorySource,
} from '@/types/custom-playbooks';

/** The vendors Job history reads from (the server's
 * `play_job_history.SOURCES`) and their names. */
export const JOB_HISTORY_SOURCES: readonly JobHistorySource[] = [
  'crustdata',
  'enrichlayer',
];
export const JOB_HISTORY_SOURCE_LABEL: Record<JobHistorySource, string> = {
  crustdata: 'Crustdata',
  enrichlayer: 'EnrichLayer',
};
/** The check value that reads one source only (`NO_VERIFY`). */
export const JOB_HISTORY_NO_CHECK = 'none';

/** What a fresh Job history step saves; equal to the server's
 * `play_job_history.DEFAULT_CONFIG`. */
export const JOB_HISTORY_DEFAULT_CONFIG: JobHistoryConfig = {
  profileSource: 'crustdata',
  verifySource: 'enrichlayer',
};

const KEYS = new Set(['profileSource', 'verifySource', 'sourceField', 'delay']);
const isSource = (value: unknown): value is JobHistorySource =>
  value === 'crustdata' || value === 'enrichlayer';

/** The vendor a source's check uses when none is saved: the other one. */
export function otherJobHistorySource(
  source: JobHistorySource
): JobHistorySource {
  return source === 'crustdata' ? 'enrichlayer' : 'crustdata';
}

/**
 * The source and the check as the server reads them
 * (`play_job_history._settings`): the source Crustdata when none is saved, and
 * the check the vendor that is not the source when none is saved.
 */
export function jobHistorySettings(
  config: Partial<JobHistoryConfig> | undefined
): { source: unknown; verify: unknown } {
  // A key saved as null is a value the server rejects, not an absent one (an
  // undefined key is not saved at all).
  const source =
    config?.profileSource !== undefined
      ? config.profileSource
      : JOB_HISTORY_DEFAULT_CONFIG.profileSource;
  if (config?.verifySource !== undefined)
    return { source, verify: config.verifySource };
  // As the server does: the first vendor that isn't the source.
  return {
    source,
    verify:
      JOB_HISTORY_SOURCES.find((vendor) => vendor !== source) ??
      JOB_HISTORY_NO_CHECK,
  };
}

/** The vendor the step checks with, or null for no check (`none`, or the
 * source itself) (`play_job_history.verify_source`). */
export function jobHistoryCheck(
  config: Partial<JobHistoryConfig> | undefined
): JobHistorySource | null {
  const { source, verify } = jobHistorySettings(config);
  return isSource(verify) && verify !== source ? verify : null;
}

/**
 * Why a Job history step can't turn On yet, mirroring the server's
 * `play_job_history.validate_config`; null when it is complete.
 */
export function jobHistoryConfigError(
  config: Partial<JobHistoryConfig> | undefined
): string | null {
  if (!config || typeof config !== 'object' || Array.isArray(config))
    return 'Set up Job history.';
  const extra = Object.keys(config)
    .filter((key) => !KEYS.has(key))
    .sort();
  if (extra.length) return `Job history doesn't take ${extra.join(', ')}.`;
  const { source, verify } = jobHistorySettings(config);
  if (!isSource(source)) return 'Read from Crustdata or EnrichLayer.';
  if (!isSource(verify) && verify !== JOB_HISTORY_NO_CHECK)
    return 'Check with Crustdata, EnrichLayer or none.';
  if (verify === source) return 'Check with the other source, or none.';
  const field = config.sourceField;
  if (
    field !== undefined &&
    !(
      typeof field === 'string' &&
      field.length > 0 &&
      field.length <= 200 &&
      field === field.trim()
    )
  )
    return 'Pick the field that holds the LinkedIn URL.';
  return null;
}

/** The card line, as the server writes it (`play_job_history.step_summary`):
 * "Crustdata · checked with EnrichLayer". */
export function jobHistorySummary(
  config: Partial<JobHistoryConfig> | undefined
): string {
  const { source } = jobHistorySettings(config);
  const label = isSource(source)
    ? JOB_HISTORY_SOURCE_LABEL[source]
    : 'Pick a source';
  const check = jobHistoryCheck(config);
  return check
    ? `${label} · checked with ${JOB_HISTORY_SOURCE_LABEL[check]}`
    : label;
}
